<template>
  <div
    class="sam-input-group"
    :class="groupedInputTextObjClass"
    data-samora
  >
    <SamSelect
      class="sam-input-group__select"
      :items="selectItems"
      :value="selectData"
      :with-clear="false"
      :disabled="disabled"
      :readonly="readonly"
      :style="selectStyleObj"
      v-bind="selectAttrs"
      @change="handleSelectChange"
    />
    <SamInput
      data-testid="ta-sam-input-group--input"
      class="sam-input-group__inputText"
      :value="inputData"
      :disabled="disabled"
      :readonly="readonly"
      :sam-style="samStyle"
      v-bind="inputAttrs"
      @input="handleInputTextInput"
      @change="handleInputTextChange"
    />
  </div>
</template>

<script>
import SAMORA_STYLES from '../../../utils/constants/samora-styles.enum';
import SamSelect from '../SamSelect/SamSelect.vue';
import SamInput from '../SamInput/SamInput.vue';
import { objectHasOwnProperties } from '../../../utils/utils';

export const INPUT_GROUP_STYLES = {
  DEFAULT: SAMORA_STYLES.DEFAULT,
  DANGER: SAMORA_STYLES.DANGER,
  SUCCESS: SAMORA_STYLES.SUCCESS,
};

export const INPUT_GROUP_SELECT_POSITIONS = {
  LEFT: 'left',
  RIGHT: 'right',
};

export default {
  name: 'SamInputGroup',
  components: {
    SamInput,
    SamSelect,
  },
  props: {
    /**
     * The options for the Select's dropdown
     */
    selectItems: {
      type: Array,
      required: true,
      default: () => [],
      validator: (val) => val.every?.((item) => objectHasOwnProperties(item, 'label', 'value')),
    },
    /**
     * The GroupedInputText Select width. Defaults to 110px CSS width
     * @values true, false
     */
    selectWidth: {
      type: Number,
      required: false,
      default: undefined,
    },
    /**
     * The GroupedInputText Select position. Defaults to 'left'
     * @values 'left', 'right'
     */
    selectPosition: {
      type: String,
      required: false,
      default: INPUT_GROUP_SELECT_POSITIONS.LEFT,
    },
    /**
     * The GroupedInputText data variable
     */
    data: {
      type: [Object, Array],
      required: false,
      default: undefined,
    },
    /**
     * If the GroupedInputText is disabled
     * @values true, false
     */
    disabled: {
      type: Boolean,
      required: false,
      default: false,
    },
    /**
     * If the GroupedInputText is on readonly state
     * @values true, false
     */
    readonly: {
      type: Boolean,
      required: false,
      default: false,
    },
    /**
     * The samora style to be applied
     * @values true, false
     */
    samStyle: {
      type: String,
      required: false,
      default: SAMORA_STYLES.DEFAULT,
      validator: (val) => Object.values(INPUT_GROUP_STYLES).includes(val),
    },
    /**
     * Attributes to be bound to the select
     */
    selectAttrs: {
      type: Object,
      required: false,
      default: () => ({}),
    },
    /**
     * Attributes to be bound to the input
     */
    inputAttrs: {
      type: Object,
      required: false,
      default: () => ({}),
    },
  },
  data() {
    return {
      selectData: undefined,
      inputData: undefined,
    };
  },
  computed: {
    groupedInputTextObjClass() {
      return {
        isActive: this.isActive,
        'is-readonly': this.readonly,
        [`is-select-pos-${this.selectPosition}`]: true,
      };
    },
    selectStyleObj() {
      if (this.selectWidth) {
        return {
          width: `${this.selectWidth}px`,
        };
      }

      return {};
    },
  },
  watch: {
    data: {
      handler(newVal) {
        if (Array.isArray(this.data)) {
          const [selectValue, inputValue] = this.data;
          this.selectData = selectValue;
          this.inputData = inputValue;
          return;
        }
        if (newVal && Object.keys(newVal).length === 1) {
          const key = Object.keys(newVal)[0];
          this.selectData = key;
          this.inputData = newVal[`${key}`];
        } else if (this.selectItems && this.selectItems[0] && this.selectItems[0].value) {
          this.selectData = this.selectItems[0].value;
        }
      },
      immediate: true,
    },
  },
  methods: {
    handleSelectChange(val) {
      this.selectData = val?.value;
      this.$emit('change', this.selectData, this.inputData);
    },
    handleInputTextInput(val) {
      this.inputData = val || undefined;
      /**
       * Triggers when GroupedInputText changes it's value during value input (@input)
       * @event input
       * @property {string} selectData new value set for the select part
       * @property {string} inputData new value set for the input part
       */
      this.$emit('input', this.selectData, this.inputData);
    },
    handleInputTextChange(val) {
      this.inputData = val || undefined;
      /**
       * Triggers when GroupedInputText changes it's value (@change)
       * @event change
       * @property {string} selectData new value set for the select part
       * @property {string} inputData new value set for the input part
       */
      this.$emit('change', this.selectData, this.inputData);
    },
  },
};
</script>

<style lang="scss" scoped>
.sam-input-group {
  @extend .d-flex;
  $select-fixed-width: 145px;
  border-radius: $size--1;

  &__select {
    width: $select-fixed-width;
  }

  &__inputText {
    flex: 1;
  }

  &.is-select-pos-left {
    flex-direction: row;

    ::v-deep {
      .sam-select__input-field {
        border-top-right-radius: 0;
        border-bottom-right-radius: 0;
        border-right: none;
      }
    }

    .sam-input-group__inputText {
      border-top-left-radius: 0;
      border-bottom-left-radius: 0;
    }
  }

  &.is-select-pos-right {
    flex-direction: row-reverse;

    ::v-deep {
      .sam-select__input-field {
        border-top-left-radius: 0;
        border-bottom-left-radius: 0;
        border-left: none;
      }
    }

    .sam-input-group__inputText {
      border-top-right-radius: 0;
      border-bottom-right-radius: 0;
    }
  }
}
</style>
